import React from "react";
import { requestAppReload } from "../system/appReloadCoordinator";

/** Erkennt typische Fehler beim Nachladen von Route-Modulen (import()). Kein reines „Deploy“-Signal: auch Proxy, Blocker, Netzwerk, 404. */
function isChunkOrUpdateError(e: unknown): boolean {
  if (typeof e !== "object" || e === null || !("message" in e)) return false;
  const msg = typeof (e as { message: unknown }).message === "string" ? (e as { message: string }).message : "";
  const m = msg.toLowerCase();
  return (
    m.includes("failed to fetch dynamically imported module") ||
    m.includes("error loading dynamically imported module") ||
    m.includes("importing a module script failed") ||
    m.includes("loading chunk") ||
    m.includes("chunkloaderror") ||
    m.includes("loading css chunk")
  );
}

const LEGACY_CHUNK_RELOAD_SESSION_KEY = "m1member.chunkLoadReloaded";
const CHUNK_RELOAD_SESSION_PREFIX = "m1member.chunkLoadReloaded.";
const CHUNK_STATE_RELOADED = "reloaded";
const FINGERPRINT_MAX_LENGTH = 180;

function chunkErrorMessage(e: unknown): string {
  return e instanceof Error ? e.message : typeof e === "string" ? e : String(e ?? "");
}

function chunkErrorFingerprint(e: unknown): string {
  const message = chunkErrorMessage(e);
  const path = typeof window !== "undefined" ? `${window.location.pathname}${window.location.hash}` : "";
  return encodeURIComponent(`${path}|${message}`).slice(0, FINGERPRINT_MAX_LENGTH);
}

function failedChunkUrl(e: unknown): string | null {
  const message = chunkErrorMessage(e);
  const match = message.match(/https?:\/\/[^\s)]+/);
  if (!match) return null;
  try {
    const url = new URL(match[0]);
    if (typeof window !== "undefined" && url.origin !== window.location.origin) return null;
    return url.toString();
  } catch {
    return null;
  }
}

async function refreshFailedChunk(e: unknown): Promise<boolean> {
  const url = failedChunkUrl(e);
  if (!url || typeof fetch !== "function") return false;
  try {
    const response = await fetch(url, { cache: "reload", credentials: "same-origin" });
    return response.ok;
  } catch {
    return false;
  }
}

function scheduleReload(e: unknown): Promise<never> {
  requestAppReload({
    reason: "chunk-load-failed",
    dedupeKey: `${chunkErrorFingerprint(e)}:${Date.now()}`
  });
  return new Promise<never>(() => {});
}

async function recoverFromChunkError(e: unknown): Promise<boolean> {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return false;
  try {
    sessionStorage.removeItem(LEGACY_CHUNK_RELOAD_SESSION_KEY);
    const key = `${CHUNK_RELOAD_SESSION_PREFIX}${chunkErrorFingerprint(e)}`;
    const state = sessionStorage.getItem(key);
    if (state == null) {
      sessionStorage.setItem(key, CHUNK_STATE_RELOADED);
      return true;
    }
    if (state === CHUNK_STATE_RELOADED && (await refreshFailedChunk(e))) {
      sessionStorage.setItem(key, "refreshed");
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function clearChunkReloadMarker(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(LEGACY_CHUNK_RELOAD_SESSION_KEY);
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CHUNK_RELOAD_SESSION_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
}

/**
 * Lazy mit Abfang von Modul-/Chunk-Ladefehlern (import()). Bei bekannten Mustern einmal Reload bzw.
 * gezielter Fetch mit cache: "reload". Ursachen sind vielfältig (Deploy-Mismatch, Proxy, Blocker, Netzwerk).
 */
export function lazyWithChunkError(
  importFn: () => Promise<{ default: React.ComponentType<any> }>
): React.LazyExoticComponent<React.ComponentType<any>> {
  return React.lazy(() =>
    importFn()
      .then((m) => {
        clearChunkReloadMarker();
        return m;
      })
      .catch(async (e: unknown) => {
        const chunkOrUpdateError = isChunkOrUpdateError(e);
        if (chunkOrUpdateError && (await recoverFromChunkError(e))) {
          return scheduleReload(e);
        }
        const messageKey = chunkOrUpdateError
          ? "i18n:Page could not be loaded after update"
          : "i18n:Page could not be loaded reload";
        const err = new Error(messageKey);
        (err as Error & { cause?: unknown }).cause = e;
        throw err;
      })
  );
}
